import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { User } from '@stash/shared-types';
import { loginAsync, logoutAsync } from '../actions/auth-actions';

export interface AuthState {
  currentUser: User | null;
  loginError: string | null;
  isLoading: boolean;
}

const initialState: AuthState = {
  currentUser: null,
  loginError: null,
  isLoading: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginUser: (state, action: PayloadAction<User>) => {
      state.currentUser = action.payload;
      state.loginError = null;
      state.isLoading = false;
    },
    logout: (state) => {
      state.currentUser = null;
      state.loginError = null;
      state.isLoading = false;
    },
    loginFailed: (state, action: PayloadAction<string>) => {
      state.loginError = action.payload;
      state.isLoading = false;
    },
    authStateChanged: (state, action: PayloadAction<User | null>) => {
      state.currentUser = action.payload;
      state.isLoading = false;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginAsync.pending, (state: AuthState) => {
        state.isLoading = true;
        state.loginError = null;
      })
      .addCase(loginAsync.fulfilled, (state: AuthState, action: PayloadAction<User>) => {
        state.currentUser = action.payload;
        state.isLoading = false;
        state.loginError = null;
      })
      .addCase(loginAsync.rejected, (state: AuthState, action: PayloadAction<string | undefined>) => {
        state.loginError = action.payload ?? null;
        state.isLoading = false;
        state.currentUser = null;
      })
      .addCase(logoutAsync.pending, (state: AuthState) => {
        state.isLoading = true;
        state.loginError = null;
      })
      .addCase(logoutAsync.fulfilled, (state: AuthState) => {
        state.currentUser = null;
        state.isLoading = false;
        state.loginError = null;
      })
      .addCase(logoutAsync.rejected, (state: AuthState, action: PayloadAction<string | undefined>) => {
        state.loginError = action.payload ?? null;
        state.isLoading = false;
      });
  },
});

export const { loginUser, logout, loginFailed, authStateChanged, setLoading } = authSlice.actions;
export default authSlice.reducer;
